import { Column } from '@tanstack/react-table';
import { ReactNode, useCallback, useMemo } from 'react';
import NotificationIcon from '../assets/icons/notification.svg?react';
import { ResponsiveDataView } from '../components/data-display/ResponsiveDataView';
import { NotificationSidePanel } from '../components/notifications';
import { Toggle } from '../components/Toggle';
import { NotificationEvent } from '../gql/generated';
import { useNotificationFilters } from '../hooks/useNotificationFilters';
import { createNotificationColumns } from './notifications/createNotificationColumns';
import { NotificationListItemMobile } from './notifications/NotificationListItemMobile';
import { useNotificationColumnFilters } from './notifications/useNotificationColumnFilters';
import { useNotificationsTable } from './notifications/useNotificationsTable';

export function NotificationHistoryPage() {
  const state = useNotificationsTable();
  const columns = useMemo(() => createNotificationColumns(), []);
  const { selectedId, setSelectedId, showAllUsers, setShowAllUsers } =
    useNotificationFilters();

  // Get the column filter render function (reads filters directly from URL)
  const renderColumnFilter = useNotificationColumnFilters() as (
    column: Column<NotificationEvent, unknown>
  ) => ReactNode;

  // Derive notification and open state from URL
  const selectedNotification = useMemo(() => {
    if (!selectedId || state.notifications.length === 0) return null;
    return state.notifications.find((n) => n.id === selectedId) ?? null;
  }, [selectedId, state.notifications]);

  // Panel is open when there's a notification ID in URL
  const sidePanelOpen = selectedId !== null;

  const handleRowClick = useCallback(
    (notification: NotificationEvent) => {
      setSelectedId(notification.id);
    },
    [setSelectedId]
  );

  const handlePanelClose = useCallback(
    (open: boolean) => {
      if (!open) {
        setSelectedId(null);
      }
    },
    [setSelectedId]
  );

  return (
    <>
      <ResponsiveDataView
        data={state.notifications}
        columns={columns}
        isLoading={state.loading}
        onRefresh={async () => {
          await state.refetch();
        }}
        error={state.error}
        getRowId={(item) => item.id}
        onRowClick={handleRowClick}
        renderListItem={(item, config) => (
          <NotificationListItemMobile event={item} config={config} />
        )}
        pageIndex={state.pageIndex}
        pageSize={state.pageSize}
        totalPages={state.totalPages}
        totalCount={state.totalCount}
        onPageChange={state.setPageIndex}
        onPageSizeChange={state.setPageSize}
        sorting={state.sorting}
        onSortingChange={state.setSorting}
        columnPinning={state.columnPinning}
        onColumnPinningChange={state.setColumnPinning}
        columnVisibility={state.columnVisibility}
        onColumnVisibilityChange={state.setColumnVisibility}
        columnOrder={state.columnOrder}
        onColumnOrderChange={state.setColumnOrder}
        columnSizing={state.columnSizing}
        onColumnSizingChange={state.onColumnSizingChange}
        resetColumnSizing={state.resetColumnSizing}
        columnFilters={renderColumnFilter}
        enableManageColumns
        toolbarActions={
          <label className='flex items-center gap-2 whitespace-nowrap'>
            <Toggle
              checked={!showAllUsers}
              onCheckedChange={(checked) => setShowAllUsers(!checked)}
              aria-label='Only show my notifications'
            />
            <span className='size-p2 text-secondary'>
              Only my notifications
            </span>
          </label>
        }
        emptyStateMessage='No notifications yet'
        ariaLabel='Notifications table'
        title='Notifications'
        icon={<NotificationIcon />}
      />
      <NotificationSidePanel
        open={sidePanelOpen}
        onOpenChange={handlePanelClose}
        notification={selectedNotification}
      />
    </>
  );
}
